import { GlobalMessages } from '@shared/localization/messages';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { useMemo, useState } from 'react';

import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import { useEstimationUserAssignmentQueryActions } from '@/api/hooks/estimationUserAssignmentQueryActions';
import type {
  EstimationUserAssignmentRequest,
  UpdateProjectWithEstimationRequest,
} from '@/api/schema/data-contracts';
import { ApiEstimationStage } from '@/api/schema/data-contracts';
import { Loader } from '@/components/atoms/Loader';
import { useAuth } from '@/context/auth';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import {
  type EstimationProjectUsers,
  useGetEstimationProjectUsers,
} from '@/hooks/useGetEstimationProjectUsers';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';

import type { ProjectBasicsModel } from '../../model';
import { ProjectForm } from './ProjectForm';

const ProjectContainer = () => {
  const { estimationId } = Route.useLoaderData();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const toast = useToast();
  const globalMessages = useMessages(GlobalMessages);

  const { tenant } = useAuth();

  const { useUpdateUserAssignments } = useEstimationUserAssignmentQueryActions();
  const { mutateAsync: assignUsersToEstimation } = useUpdateUserAssignments();
  const { useUpdateEstimation } = useEstimationQueryActions();
  const { mutateAsync: updateEstimation } = useUpdateEstimation();

  const { data: estimationData, isLoading } = useGetEstimationProjectUsers(estimationId) as {
    data: EstimationProjectUsers | undefined;
    isLoading: boolean;
  };

  const hasEditPermission = useHasEstimationPermission(estimationId, 'CAN_EDIT');

  const estimationProjectUpdateData: UpdateProjectWithEstimationRequest | undefined =
    useMemo(() => {
      if (estimationData) {
        return {
          project: {
            name: estimationData.project.name,
          },
          estimation: {
            versionName: estimationData.estimation.versionName,
            unit: estimationData.estimation.unit,
            planningResponsibleId: estimationData.estimation.planningResponsibleId ?? '',
          },
        };
      }
    }, [estimationData]);

  if (isLoading) {
    return <Loader />;
  }

  if (estimationData === undefined || tenant === null) {
    return <Loader />;
  }

  const onSubmit = async (model: ProjectBasicsModel): Promise<void> => {
    if (!estimationProjectUpdateData) {
      throw new Error('Form must be initialized');
    }

    if (!model.planningResponsibleId) {
      throw new Error('Planning responsible is undefined');
    }

    const existingUserAssignments = sortBy(
      estimationData.users.map<EstimationUserAssignmentRequest>((u) => ({
        userId: u.id,
        assignmentType: u.estimationUserAssignmentType,
      })),
      ['userId', 'assignmentType'],
    );

    const userAssignmentsForUpdate: EstimationUserAssignmentRequest[] = sortBy(
      [
        ...model.additionalContributorIds.map<EstimationUserAssignmentRequest>((contributorId) => ({
          userId: contributorId,
          assignmentType: 'CONTRIBUTOR',
        })),
        ...(model.planningResponsibleId
          ? [
              {
                userId: model.planningResponsibleId,
                assignmentType: 'PLANNING_RESPONSIBLE',
              } as EstimationUserAssignmentRequest,
            ]
          : []),
        ...(model.projectReviewerId
          ? [
              {
                userId: model.projectReviewerId,
                assignmentType: 'REVIEWER',
              } as EstimationUserAssignmentRequest,
            ]
          : []),
      ],
      ['userId', 'assignmentType'],
    );

    const userAssignmentsChanging = !isEqual(existingUserAssignments, userAssignmentsForUpdate);

    const promises = [];
    if (model.projectName !== estimationData.project.name) {
      promises.push(
        updateEstimation({
          estimationId,
          updateProjectWithEstimation: {
            estimation: {
              ...estimationProjectUpdateData.estimation,
            },
            project: {
              name: model.projectName,
            },
          },
        }),
      );
    }

    if (userAssignmentsChanging) {
      promises.push(
        assignUsersToEstimation({
          estimationId: estimationId,
          estimationUserAssignments: userAssignmentsForUpdate,
        }),
      );
    }

    try {
      setIsProcessing(true);
      if (promises.length > 0) {
        await Promise.all(promises).then(() => {
          toast.showToast(globalMessages.savedSuccessfully(), 'success');
        });
      }
    } finally {
      setIsProcessing(false);
    }

    return Promise.resolve();
  };

  const isReadOnly =
    estimationData.estimation.stage === ApiEstimationStage.IN_REVIEW ||
    estimationData.estimation.stage === ApiEstimationStage.APPROVED ||
    !hasEditPermission;

  return (
    <ProjectForm
      tenant={tenant}
      estimationData={estimationData}
      onSubmit={onSubmit}
      isReadOnly={isReadOnly}
      isProcessing={isProcessing}
    />
  );
};

export default ProjectContainer;
